import { i18n } from '@lingui/core';
import WarpElement from '@warp-ds/elements-core';
import { css, html } from 'lit';
import pulse from '@finn-no/pulse-sdk';
import { setupOverflowGradient} from './utils/gradient-utils.js';

// SEO categories section.
export class CategoriesSection extends WarpElement {
    static styles = [
        ...WarpElement.styles, // Adds the global styles
        css`
            /** Component styles which are not available outside this component **/
            @media (max-width: 989px) {
                .categories-overflow-gradient {
                    background: linear-gradient(90deg, rgba(214, 214, 214, 0) 0%, rgba(121, 121, 121, 0) 76%, rgba(255, 255, 255, 1) 100%);
                }
            }
            *,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0}@media (width>=990px){.lg\:t5{font-weight:700;font-size:var(--w-font-size-s);line-height:var(--w-line-height-s)}.lg\:text-caption{font-size:var(--w-font-size-s);line-height:var(--w-line-height-s)}}.gap-y-6{row-gap:.6rem}.gap-x-\[32px\]{column-gap:32px}.auto-cols-\[22rem\]{grid-auto-columns:22rem}.grid-rows-\[repeat\(auto-fit\,minmax\(2\.8rem\,1fr\)\)\]{grid-template-rows:repeat(auto-fit,minmax(2.8rem,1fr))}.overflow-x-auto{overflow-x:auto}.static{position:static}.h-28{height:2.8rem}.min-h-\[24px\]{min-height:24px}.w-\[calc\(100\%_-_1\.6rem\)\]{width:calc(100% - 1.6rem)}.my-48{margin-top:4.8rem;margin-bottom:4.8rem}.pb-6{padding-bottom:.6rem}.truncate{text-overflow:ellipsis;white-space:nowrap;overflow:hidden}.leading-l{line-height:var(--w-line-height-l)}@media (width<=989.9px){.lt-lg\:gap-y-2{row-gap:.2rem}.lt-lg\:max-h-\[72vh\]{max-height:72vh}}@media (width>=990px){.lg\:grid-rows-\[repeat\(35\,1\.8rem\)\]{grid-template-rows:repeat(35,1.8rem)}.lg\:grid-rows-\[repeat\(42\,1\.8rem\)\]{grid-template-rows:repeat(42,1.8rem)}.lg\:overflow-hidden{overflow:hidden}.lg\:static{position:static}.lg\:h-auto{height:auto}.lg\:w-auto{width:auto}.lg\:h-\[18\]{height:1.8rem}.lg\:max-w-\[21ch\]{max-width:21ch}.lg\:pointer-events-auto{pointer-events:auto}.lg\:leading-\[24px\]{line-height:24px}};
        `,
    ];

    connectedCallback() {
        super.connectedCallback();
        setupOverflowGradient({
            gradientSelector: '.categories-overflow-gradient',
            containerSelector: '.categories-scrollable-container',
            scrollableSelector:'.scrollable-content',
            root: this.shadowRoot
        });
    }

    // Track the click on a category (used for both main (header) sections and subsections).
    trackCategoryClick(trackingData) {
        pulse.trackEvent({
            type: 'Click',
            intent: 'Open',
            name: trackingData.name,
            object: {
                type: 'UIElement',
                elementType: trackingData.object.element_type,
                name: trackingData.object.name,
            },
            target: trackingData.target,
            vertical: {
                '@type': trackingData.vertical.type,
                name: trackingData.vertical.name,
            },
        });
    }

    get _rowClass() {
        return this.brand === 'DBA' ? 'lg:grid-rows-[repeat(42,1.8rem)]' : 'lg:grid-rows-[repeat(35,1.8rem)]';
    }

    static properties = {
        brand: { type: String, attribute: 'brand', reflect: true },
        sections: { type: Array, attribute: 'sections', reflect: true },
    };

    render() {
        // HTML rows.
        const items = [];

        if (this.sections) {
            const { sections } = this;

            // Create html items from sections and categories.
            sections.forEach((section) => {
                const { tracking, destinations, label } = section.header_category;
                const textColor = `${section.categories?.length === 0 ? 's-text-link' : 's-text'}`;

                items.push(
                    html`<h2 class="flex flex-col justify-center min-h-[24px]">
                        <a
                            class="truncate s-text t4 leading-l lg:t5 lg:leading-[24px] lg:max-w-[21ch] ${textColor}"
                            href=${destinations.url}
                            @click=${() => this.trackCategoryClick(tracking)}
                        >
                            ${label}
                        </a>
                    </h2> `,
                );

                section.categories.forEach((category) => {
                    items.push(
                        html`<div class="flex flex-col justify-center min-h-[24px]">
                            <a
                                href="${category.destinations.url}"
                                class="truncate text-m leading-l lg:text-caption lg:leading-[24px] lg:max-w-[21ch]"
                                @click=${() => this.trackCategoryClick(category.tracking)}
                            >
                                ${category.label}
                            </a>
                        </div> `,
                    );
                });

                if (sections.indexOf(section) < sections.length - 1) {
                    items.push(html`<span class="h-28 lg:h-[18]"></span>`);
                }
            });

            return html`
                      <section class="my-48">
                          <h1 class="sr-only">
                              ${i18n.t({
                                  id: 'categoriesSection.screenreaderOnly.h1',
                                  message: 'Categories section',
                                  comment: 'Screen reader only header for categories section',
                              })}
                          </h1>
                          <div class="categories-scrollable-container">
                              <div class="relative">
                                  <div class="scrollable-content overflow-x-auto lg:overflow-hidden">
                                      <div class="categories-overflow-gradient absolute right-0 top-0 w-[calc(100%_-_1.6rem)] h-full z-10 pointer-events-none lg:static lg:pointer-events-auto lg:h-auto lg:w-auto"></div>
                                      <div class="pl-4 pb-6 lt-lg:max-h-[72vh] grid grid-flow-col grid-rows-[repeat(auto-fit,minmax(2.8rem,1fr))] auto-cols-[22rem] gap-x-[32px] lt-lg:gap-y-2 gap-y-6 ${this._rowClass}" data-categories-pulse-wrapper>
                                          ${items}
                                      </div>
                                  </div>
                              </div>
                          </div>
                      </section>
                  `;
        }
    }
}

if (!customElements.get('categories-section')) {
    customElements.define('categories-section', CategoriesSection);
}
